import { sum, unique } from 'lib/array/arrayUtils'

export const ACCEPT_EXTENSIONS = '.png,.jpeg,.jpg,.webp,.gif,.pdf,.csv,.txt'

/**
 * Must stay under svc-trip's `express.json({ limit: '10mb' })` on POST /v3/messages.
 * Attachments are sent base64 (4 bytes on the wire per 3 on disk) and the limit is on
 * the whole body, so this is one budget shared by every attachment on a message, not a
 * per-file cap. 10MB of body is ~7.5MB of real bytes; the rest is headroom for the
 * message text.
 */
export const TOTAL_CHAT_FILE_SIZE_LIMIT_BYTES = 7 * 1024 * 1024

const ACCEPTED_EXTENSIONS_LIST = ACCEPT_EXTENSIONS.split(',').map(ext => ext.toLowerCase().trim())

export const ACCEPTED_FILE_FORMAT_LABELS = unique(ACCEPTED_EXTENSIONS_LIST.map(ext => ext.slice(1).toUpperCase()))

const ACCEPTED_EXTENSION_BY_MIME_TYPE: Partial<Record<string, string>> = {
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/webp': '.webp',
  'image/gif': '.gif',
  'application/pdf': '.pdf',
  'text/csv': '.csv',
  'text/plain': '.txt',
}

const getFileExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf('.')
  return lastDotIndex >= 0 ? fileName.toLowerCase().substring(lastDotIndex) : ''
}

const isValidChatFileExtension = (fileName: string): boolean =>
  ACCEPTED_EXTENSIONS_LIST.includes(getFileExtension(fileName))

/** Names a file the checks can read: adds the extension its type implies, and a name when it has none. */
export function normaliseChatFile(file: File, index: number): File {
  if (getFileExtension(file.name)) {
    return file
  }

  const extension = ACCEPTED_EXTENSION_BY_MIME_TYPE[file.type.toLowerCase()]
  if (file.name && !extension) {
    return file
  }

  const name = file.name || `file-${index + 1}`
  return new File([file], `${name}${extension ?? ''}`, {
    type: file.type,
    lastModified: file.lastModified,
  })
}

export function getChatFileKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`
}

export function hasDraggedFiles(dataTransfer: DataTransfer | null): boolean {
  return Array.from(dataTransfer?.types ?? []).some(type => type === 'Files' || type === 'application/x-moz-file')
}

export function partitionChatFiles(files: Array<File>): { validFiles: Array<File>; invalidFileNames: Array<string> } {
  const validFiles: Array<File> = []
  const invalidFileNames: Array<string> = []

  files.forEach((file) => {
    if (isValidChatFileExtension(file.name)) {
      validFiles.push(file)
    } else {
      invalidFileNames.push(file.name)
    }
  })

  return { validFiles, invalidFileNames }
}

export function exceedsChatFileSizeLimit(files: Array<File>): boolean {
  return sum(files, file => file.size) > TOTAL_CHAT_FILE_SIZE_LIMIT_BYTES
}
